// React Imports
import { ReactElement, useEffect, useState } from "react";

// Third Party Libraries Imports
import { Link, useParams } from "react-router-dom";

// Local Imports
import CustomModal from "../../components/custom-modal/custom-modal";
import BreadcrumbComponent from "../../components/breadcrumb-component/breadcrumb-component";
import TakeDepositActionService from "./take-deposit-action.service";
import CollapseComponent from "../../components/collapse.component";
import moment from "moment";
import TakeAction from "../../components/take-action/take-action";
import clsx from "clsx";
import ReactCountryFlag from "react-country-flag";
import { Divider, Image } from "antd";
import { useSelector } from "react-redux";
import { extractToEntity } from "src/utils/helper/functions";

const TakeDepositAction: React.FC = (): ReactElement => {
  const user = useSelector((state: any) => state?.user);
  const takeDepositActionService = new TakeDepositActionService();
  const { depositId }: { [key: string]: string | undefined } = useParams();
  const [openApproveModal, setOpenApproveModal] = useState<boolean>(false);
  const [openRejectModal, setOpenRejectModal] = useState<boolean>(false);
  const [depositDetailsData, setDepositDetailsData] = useState<any>();

  useEffect(() => {
    fetchPortfolioDetailsData();
  }, []);

  const onTakeAction = async (action: string, value: string) => {
    const response: string | undefined =
      await takeDepositActionService.takeAction(depositId, action, value);
    if (response === "success") {
      fetchPortfolioDetailsData();
      setOpenApproveModal(false);
      setOpenRejectModal(false);
    }
  };

  const fetchPortfolioDetailsData = async (): Promise<void> => {
    const data = await takeDepositActionService.getDepositDetailsData(
      depositId
    );

    if (data !== undefined) {
      setDepositDetailsData(data);
    }
  };

  return (
    depositDetailsData && (
      <section className="flex flex-col">
        <BreadcrumbComponent />
        <div className="flex flex-col pb-7 flex-grow">
          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">Portfolio details</h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account#
                </p>
                <Link
                  to={`/home/business-account/${depositDetailsData?.portfolio?.account?.id}/${depositDetailsData?.portfolio?.account?.activeKycId}/details`}
                  className=" text-base font-semibold text-lightGray-200 underline"
                >
                  {depositDetailsData?.portfolio?.account?.accountCode}
                </Link>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {
                    depositDetailsData?.portfolio?.account?.clientType
                      ?.description
                  }{" "}
                  -{" "}
                  {
                    depositDetailsData?.portfolio?.account?.clientSubType
                      ?.description
                  }
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Account status
                </p>
                <span
                  className={clsx(
                    "text-base font-semibold w-fit px-3 py-1 rounded-md  text-white",
                    depositDetailsData?.portfolio?.account?.accountStatus?.description
                      ?.toLowerCase()
                      .includes("closed")
                      ? "bg-red-200"
                      : depositDetailsData?.portfolio?.account?.accountStatus?.description
                          ?.toLowerCase()
                          .includes("pending")
                      ? "bg-yellow-500"
                      : "bg-green-100"
                  )}
                >
                  {
                    depositDetailsData?.portfolio?.account?.accountStatus
                      ?.description
                  }
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio#
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.portfolio?.status !== "Rejected"
                    ? depositDetailsData?.portfolio?.accountCode ||
                      depositDetailsData?.portfolio?.account?.accountCode +
                        " - New"
                    : "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio type
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.portfolio?.managementType}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio status
                </p>
                <span
                  className={clsx(
                    "text-base font-semibold w-fit px-3 py-1 rounded-md text-white",
                    depositDetailsData?.portfolio?.status === "Opened"
                      ? "bg-green-100"
                      : depositDetailsData?.portfolio?.status === "Rejected"
                      ? "bg-red-200"
                      : "bg-yellow-500"
                  )}
                >
                  {depositDetailsData?.portfolio?.status === "Pending"
                    ? "New"
                    : depositDetailsData?.portfolio?.status}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Portfolio currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.portfolio?.portfolioCurrency || "-"}
                </span>
              </div>
              {depositDetailsData?.portfolio?.openDate && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Open date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {moment(depositDetailsData?.portfolio?.openDate).format(
                      "DD MMM, YYYY"
                    )}
                  </span>
                </div>
              )}

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.portfolio?.account?.wealthManager?.ae1 ||
                    "-"}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Wealth manager email
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.portfolio?.account?.wealthManager
                    ?.ae1Email || "-"}
                </span>
              </div>
              {depositDetailsData?.portfolio?.account?.wealthManager?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.portfolio?.account?.wealthManager
                      ?.ae2 || "-"}
                  </span>
                </div>
              )}
              {depositDetailsData?.portfolio?.account?.wealthManager?.ae2 && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Wealth manager email
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.portfolio?.account?.wealthManager
                      ?.ae2Email || "-"}
                  </span>
                </div>
              )}

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Online trading
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.portfolio?.onlineTradeSys ? "Yes" : "No"}
                </span>
              </div>

              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Pledged
                </p>
                <span
                  className={clsx(
                    "rounded-md w-fit",
                    depositDetailsData?.portfolio?.portfolioDisclaimer
                      ? "bg-[#FFAF00] text-white text-sm font-medium p-2"
                      : "bg-transparent text-lightGray-200 font-semibold text-base p-0"
                  )}
                >
                  {depositDetailsData?.portfolio?.portfolioDisclaimer
                    ? extractToEntity(
                        depositDetailsData?.portfolio?.portfolioDisclaimer
                      )
                    : "No"}
                </span>
              </div>
            </div>
          </CollapseComponent>

          <CollapseComponent
            header={
              <div className="flex gap-3 items-center">
                <span className="w-[5px] bg-blue-500 h-[25px] rounded-[10px]"></span>
                <h3 className=" text-xl font-semibold">
                  Deposit details{" "}
                  {depositDetailsData?.status === "Approved" ||
                  depositDetailsData?.status === "Rejected" ||
                  depositDetailsData?.portfolio?.status === "Rejected" ? (
                    <span
                      className={clsx(
                        "text-base font-semibold w-fit px-3 py-2 ms-3 rounded-md  text-white",
                        depositDetailsData?.status === "Opened" ||
                          depositDetailsData?.status === "Approved"
                          ? "bg-green-100"
                          : "bg-red-200"
                      )}
                    >
                      {depositDetailsData?.portfolio?.status === "Rejected"
                        ? "Cancelled"
                        : depositDetailsData?.status}
                    </span>
                  ) : (
                    ""
                  )}
                </h3>
              </div>
            }
          >
            <div className="flex gap-5 flex-wrap mb-5">
              <div className="w-[32%] flex flex-col">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Amount
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.amount}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Currency
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.currency}
                </span>
              </div>
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Payment method
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.paymentMethod}
                </span>
              </div>

              {depositDetailsData?.paymentMethod === "Knet" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Payment ID
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.gatewayPaymentId}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "Knet" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Reference ID
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.gatewayRef}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "Knet" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Result
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.gatewayResult}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "Knet" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Tracking ID
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.gatewayTrackId}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "Knet" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Transaction ID
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.gatewayTransId}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "Knet" &&
                depositDetailsData?.card?.cardNumber && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Card number
                    </p>
                    <div className="flex items-center gap-2">
                      <span className=" text-base font-semibold text-lightGray-200">
                        {depositDetailsData?.card?.cardNumber}
                      </span>
                      {!depositDetailsData?.card?.cardImage && (
                        <img src={`/images/tick-circle.svg`} alt="" />
                      )}
                    </div>
                  </div>
                )}

              {depositDetailsData?.paymentMethod === "Knet" &&
                depositDetailsData?.card?.cardHolderName && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Name on card
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {depositDetailsData?.card?.cardHolderName}
                    </span>
                  </div>
                )}
              {depositDetailsData?.paymentMethod === "Knet" &&
                depositDetailsData?.card?.cardExpiryDate && (
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Expiry date
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {depositDetailsData?.card?.cardExpiryDate}
                    </span>
                  </div>
                )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <Divider />
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <h3 className="text-lg w-full font-semibold">Kamco bank</h3>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Bank name
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.kamcoBank?.accountName}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Country
                  </p>
                  <div className="flex items-center gap-2">
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        depositDetailsData?.kamcoBank?.country?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {depositDetailsData?.kamcoBank?.country?.nameEn || "-"}
                    </span>
                  </div>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    IBAN
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.kamcoBank?.iban}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Swift code
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.kamcoBank?.swiftCode}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Bank referenece
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.kamcoBank?.refrence}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <Divider />
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <h3 className="text-lg w-full font-semibold">Client bank</h3>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Bank name
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.paymentBank?.bank?.bankName || "-"}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Country
                  </p>
                  <div className="flex items-center gap-2">
                    <ReactCountryFlag
                      style={{
                        fontSize: "24px",
                      }}
                      countryCode={
                        depositDetailsData?.paymentBank?.accountCountry
                          ?.countryCode
                      }
                      svg
                    />
                    <span className=" text-base font-semibold text-lightGray-200">
                      {depositDetailsData?.paymentBank?.accountCountry
                        ?.nameEn || "-"}
                    </span>
                  </div>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    IBAN
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.paymentBank?.ibanNumber || "-"}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Swift code
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {depositDetailsData?.paymentBank?.swiftCode || "-"}
                  </span>
                </div>
              )}
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <Divider />
              )}

              {(depositDetailsData?.paymentCardFront ||
                depositDetailsData?.transferReceipt ||
                depositDetailsData?.card?.cardImage) && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Attachment
                  </p>
                  <Image
                    src={
                      depositDetailsData?.paymentCardFront ||
                      depositDetailsData?.transferReceipt ||
                      depositDetailsData?.card?.cardImage
                    }
                    width={"80px"}
                    height={"50px"}
                    alt=""
                  />
                </div>
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Client notes
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.remarks || "-"}
                </span>
              </div>
              {depositDetailsData?.paymentMethod === "WireTransfer" && (
                <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                  <p className=" text-black-300 text-xs font-normal mb-[10px]">
                    Request date
                  </p>
                  <span className=" text-base font-semibold text-lightGray-200">
                    {moment(depositDetailsData?.createdAt).format(
                      "DD MMM, YYYY hh:mm A"
                    )}
                  </span>
                </div>
              )}
              <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                <p className=" text-black-300 text-xs font-normal mb-[10px]">
                  Deposit date
                </p>
                <span className=" text-base font-semibold text-lightGray-200">
                  {depositDetailsData?.depositDate
                    ? moment(depositDetailsData?.depositDate).format(
                        "DD MMM, YYYY hh:mm A"
                      )
                    : "-"}
                </span>
              </div>
              {(depositDetailsData?.status === "Approved" ||
                depositDetailsData?.status === "Rejected") && (
                <>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      {depositDetailsData?.status === "Approved"
                        ? "Prospero deposit transaction ID"
                        : "Status reason"}
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {depositDetailsData?.status === "Approved"
                        ? depositDetailsData?.transactionId
                        : depositDetailsData?.statusReason}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Action by
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {depositDetailsData?.actionBy?.name}
                    </span>
                  </div>
                  <div className="w-[100%] md:w-[48%] lg:w-[32%] flex flex-col mb-3">
                    <p className=" text-black-300 text-xs font-normal mb-[10px]">
                      Action date
                    </p>
                    <span className=" text-base font-semibold text-lightGray-200">
                      {moment(depositDetailsData?.actionDate).format(
                        "DD MMM, YYYY hh:mm A"
                      )}
                    </span>
                  </div>
                </>
              )}
            </div>
            {depositDetailsData?.status !== "Approved" &&
              depositDetailsData?.status !== "Rejected" &&
              depositDetailsData?.portfolio?.status !== "Pending" &&
              depositDetailsData?.portfolio?.status !== "Rejected" &&
              user?.permissions?.includes("request_deposit_approver") && (
                <div className="flex gap-7 justify-end items-center">
                  <button
                    onClick={() => setOpenRejectModal(true)}
                    className="rounded-lg bg-white w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-blue-500"
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => setOpenApproveModal(true)}
                    className="rounded-lg bg-blue-500 w-1/2 md:w-fit px-0 md:px-14 py-3 text-center outline-0 cursor-pointer text-xs md:text-sm font-semibold border-[1px] border-blue-500 text-white"
                  >
                    Approve deposit
                  </button>
                </div>
              )}
          </CollapseComponent>
        </div>

        <CustomModal
          isVisible={openApproveModal}
          centered
          width={450}
          onClose={() => {
            setOpenApproveModal(false);
          }}
        >
          <TakeAction
            label="Prospero deposit transaction ID"
            action="approve"
            fieldName="transactionId"
            errorName="deposit transaction ID"
            onCancel={() => setOpenApproveModal(false)}
            onSubmit={(value) => onTakeAction("approve", value)}
          />
        </CustomModal>

        <CustomModal
          isVisible={openRejectModal}
          centered
          width={450}
          onClose={() => {
            setOpenRejectModal(false);
          }}
        >
          <TakeAction
            label="Rejection reason"
            action="reject"
            fieldName="statusReason"
            errorName="reason"
            onCancel={() => setOpenRejectModal(false)}
            onSubmit={(value) => onTakeAction("reject", value)}
          />
        </CustomModal>
      </section>
    )
  );
};

export default TakeDepositAction;
